import React, { useState } from 'react';
import { Stack, Typography, Collapse, IconButton, Box } from '@mui/material';
import LoadingIndicator from '../../LoadingIndicator';
import { useTheme } from '@mui/system';
import ItemEntry from '../CardItems/ItemEntry';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import Optimization from '../CardItems/Optimizations/Optimization';
import OptimizationBadge from './OptimizationBadge';
import ComparisonView from '../PeerComparison/ComparisonCategoryEntry';
import combineItemsAndRecs from '../CombineItemsAndRecs';

function CategoryView({ physicianDraft, peerDraft, isLoading, variant, seeItemDetails, defaultCategory = null, physicianNames = {}, selectedItemId = null }) {
    const theme = useTheme();
    
    if ((variant === 'peer_draft' || variant === 'comparison') && !peerDraft) {
        return <Stack alignItems="center" justifyContent="center" sx={{ height: '50vh' }}><Typography>Please select a peer to view their card.</Typography></Stack>;
    }

    if (isLoading) return <LoadingIndicator />;

    const primaryDraft = variant === 'peer_draft' ? peerDraft : physicianDraft;
    const secondaryDraft = (variant === 'comparison' || variant === 'analytics_comparison') ? peerDraft : {};
    // In the case of comparison, we don't want to show recs, and if there is an addition rec 
    // that has a category not on either card, it is okay to not show it since it isn't part of the original draft and again, 
    // we are not showing recs, just comparing the two drafts in their original state.
    const recommendations = (variant !== 'peer_draft' && variant !== 'analytics_comparison' && variant !== 'analytics_single') ? physicianDraft.recommendations : {};
    // union of categories
    const categoryList = (Object.keys(primaryDraft.items || {})
        .concat(Object.keys(secondaryDraft.items || {}))
        .concat(Object.keys(recommendations || {}))
        .filter((category, index, arr) => arr.indexOf(category) === index))
        .sort();

    const [openCategory, setOpenCategory] = useState(defaultCategory);

    return (
        <Stack spacing={0.5} m={1}>
            {categoryList.length === 0 && (
                <Stack alignItems="center" justifyContent="center" p={4}>
                    <Typography variant="normal" fontSize={'14px'} color="text.secondary">
                        No items found in the selected cards.
                    </Typography>
                </Stack>
            )}
            {categoryList.map((category) => {
                const items = primaryDraft.items[category] || [];
                const categoryRecommendations = recommendations?.[category] || [];
                const optimizationCount = categoryRecommendations.filter((rec) => rec.type !== 'swap' && rec.type !== 'compliance' && rec.status === 'open').length;
                return (
                    <Box key={category}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            px={2}
                            py={1.5}
                            mt={openCategory === category ? 1 : 0}
                            sx={{
                                background: theme.palette.background.white,
                                borderRadius: 1,
                            }}
                            onClick={() => setOpenCategory(openCategory === category ? null : category)}
                        >
                            <Stack direction="row" alignItems="center" spacing={1}>
                                { categoryRecommendations.length > 0 && variant === 'physician_draft' &&
                                    <OptimizationBadge optimizationCount={optimizationCount} />
                                }
                                <Typography variant="heavy" fontSize='14px' color={openCategory === category ? theme.palette.blue.main : theme.palette.text.primary}>{category}</Typography>
                            </Stack>
                            <IconButton size="small">
                                <ExpandMoreRoundedIcon sx={{ transform: openCategory === category ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', color: openCategory === category ? theme.palette.blue.main : theme.palette.text.secondary }} />
                            </IconButton>
                        </Stack>
                        <Collapse in={openCategory === category} timeout="auto" unmountOnExit>
                            { (variant === 'physician_draft' || variant === 'peer_draft' || variant === 'analytics_single') ? 
                                <CategoryItems items={items} recommendations={categoryRecommendations} draft={primaryDraft} seeItemDetails={seeItemDetails} selectedItemId={selectedItemId} /> :
                                <ComparisonView physicianItems={physicianDraft.items[category]} physicianRecommendations={categoryRecommendations} peerItems={peerDraft.items[category]} seeItemDetails={seeItemDetails} physicianNames={physicianNames} selectedItemId={selectedItemId} />
                            }
                        </Collapse>
                    </Box>
                );
            })}
        </Stack>
    );
}

export default CategoryView; 

function CategoryItems({ items, recommendations = [], draft, seeItemDetails, selectedItemId = null }) {
    const itemsWithRecs = combineItemsAndRecs(items, recommendations, false, false);

    return (
        <Stack spacing={1} p={1}>
            {itemsWithRecs.map(({ item, rec }, index) => {
                return rec ? (
                    <Optimization key={index} propItem={item} recommendation={rec} draft={draft} openItemView={seeItemDetails} hideContext />
                ) : (
                    <ItemEntry key={index} item={item} draft={draft} openItemView={seeItemDetails} isSelected={selectedItemId === item.id} />
                );
            })}
        </Stack>
    );
}